// @flow
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {Slide} from 'components/Animation';
import ButtonIndex from 'components/ButtonIndex';
import {BackgroundImage} from 'components/Image';
import {LayoutColumn, LayoutWrapper, useLayoutState} from 'components/Layout';
import {Paragraph} from 'components/Localized';
import Logo from 'components/Logo';
import Disclaimer from 'components/Disclaimer';
import {useSessionState, useSessionDispatch} from 'components/Session';
import Settings from 'config/Settings';
import SoundToggleButton from 'components/SoundToggleButton';

type Props = {
  onNextA?: () => void,
  onNextB?: () => void,
  isHidden: boolean,
  onOpenModalIndex?: () => void,
}

export default function Map(props: Props): React.Element<any> {
  const location = useLocation();
  const sessionState = useSessionState();
  const sessionDispatch = useSessionDispatch();
  const [showButton, setShowButton] = React.useState(false);
  const onNextA = props.onNextA;
  const onNextB = props.onNextB;

  const cv = React.useMemo(() => {
    return location.pathname.indexOf('course-a') > 0 ? 0 : 1
  }, [location]);

  const handleMore = React.useCallback(() => {
    const link = Settings.reserve_now;
    if (link) {
      window.open(link, "_blank");
      window.tagManager.drivingZoneCourseMapExplorerMoreEvent();
    }
    
  }, []);

  const handleA = React.useCallback(() => {
    if (onNextA != null) {
      onNextA();
    }
    window.tagManager.drivingZoneCourseMapAnotherCourseEGTEvent();
  }, [onNextA]);

  const handleB = React.useCallback(() => {
    if (onNextB != null) {
      onNextB();
    }
    window.tagManager.drivingZoneCourseMapAnotherCourseGTLineEvent();
  }, [onNextB]);

  const handleOpenModalIndex = React.useCallback(() => {
    props.onOpenModalIndex?.();
  }, [props])

  React.useEffect(() => {
    sessionDispatch({type: 'SKIP_GUIDE', data: cv === 0 ? 'map-egt' : 'map-gtl'});
    setShowButton(true)
    //eslint-disable-next-line
  }, [])
  React.useEffect(()=>{
    if (location.pathname.indexOf('course-a') >= 0 ) {
      window.tagManager.drivingZoneCourseAMapPage();
    } else {
      window.tagManager.drivingZoneCourseBMapPage();
    }
    
  },[location.pathname]);



  return (
    <React.Fragment>
      <Back><BackgroundImage source={cv === 0 ? "./c00/img-c-00-bg-egt.jpg" : "./c00/img-c-00-bg-gtl.jpg"} /></Back>
      <Wrapper isHidden={props.isHidden}>
        <div><Paragraph textKey="map_c00_1_002" /></div>
        <div>
          <DivMenuButton>
            <ButtonEGT show={showButton} delay={0} onClick={handleA} visibleStamp={sessionState.skipGuide?.includes('map-egt')} />
            <ButtonGTL show={showButton} delay={300} onClick={handleB} visibleStamp={sessionState.skipGuide?.includes('map-gtl')} />
          </DivMenuButton>
        </div>
        <div>
          <ReservationParagraph textKey="map_c00_1_001"/  >
          <ReservationButton onClick={handleMore}>
            <Paragraph textKey="map_c00_1_008"/>
          </ReservationButton>
        </div>
        <div></div>
      </Wrapper>
      {cv !== 0 && <ButtonIndex onOpenModalIndex={handleOpenModalIndex} />}
      <SoundToggleButton />
      <Logo />
      <Disclaimer textKey="disclaimer_default" />
    </React.Fragment>
  );
}

type PropsButton = {
  show: boolean,
  delay: number,
  onClick?: () => void,
  visibleStamp: boolean,
}

function ButtonEGT(props: PropsButton) {
  const layoutState = useLayoutState();
  return (
    <Slide
      appear={true}
      in={props.show}
      direction="left"
      timeout={300}
      delay={props.delay}
      defaultStyle={{
        transitionDelay: props.delay ?? 0,
        opacity: 0.2,
      }}
      transitionStyles={{
        entering: {opacity: 1},
        entered: {opacity: 1},
        exiting: {opacity: 0.2},
        exited: {opacity: 0.2},
      }}
      scale={layoutState.scale}
      >
    <MenuButton onClick={props.onClick}>
      <BackgroundImage source="./c00/img-c-00-btn-egt.png">
        <Paragraph textKey="map_c00_1_004"/>
      </BackgroundImage>
      <DivStamp source="./c00/img-completed-mark.png" datavisible={props.visibleStamp}/>
    </MenuButton>
    </Slide>
  )
}

function ButtonGTL(props: PropsButton) {
  const layoutState = useLayoutState();
  return (
    <Slide
      appear={true}
      in={props.show}
      direction="left"
      timeout={300}
      delay={props.delay}
      defaultStyle={{
        transitionDelay: props.delay ?? 0,
        opacity: 0.2,
      }}
      transitionStyles={{
        entering: {opacity: 1},
        entered: {opacity: 1},
        exiting: {opacity: 0.2},
        exited: {opacity: 0.2},
      }}
      scale={layoutState.scale}
      >
    <MenuButton onClick={props.onClick}>
      <BackgroundImage source="./c00/img-c-00-btn-gtl.png">
      <Paragraph textKey="map_c00_1_005"/>
      </BackgroundImage>
      <DivStamp source="./c00/img-completed-mark.png" datavisible={props.visibleStamp}/>
    </MenuButton>
    </Slide>
  )
}

const Back = styled(LayoutWrapper)`
  background-color: #000000;

  >div {
    width: 100%;
    height: 100%;

    background-size: cover;
    background-position: center center;
  }
`;

const Wrapper = styled(LayoutColumn)`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  opacity: ${props => props.isHidden ? 0 : 1};

  >:nth-child(1) {
    flex: ${props => props.theme.layout.mobile ? 1.13 : 1.11};
    display: flex;
    align-items: center;
    justify-content: center;

    text-align: center;
    > p {
      padding-top: ${props => props.theme.layout.mobile ? 18 : 70}px;
    }
  }
  >:nth-child(2) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  >:nth-child(3) {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
  }
  >:nth-child(4) {
    height: ${props => props.theme.layout.mobile ? 35 : 39}px;
  }
`;

const DivMenuButton = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${props => Settings.reserve_now != null ? "0" : (props.theme.layout.mobile?"50":"80")  }px;

  >:last-child {
    margin-left: ${props => props.theme.layout.mobile ? -6 : -12}px;
  }
`;

const MenuButton = styled.button`
  width: ${props => props.theme.layout.mobile ? 281 : 452}px;
  height: ${props => props.theme.layout.mobile ? 122 : 198}px;
  position: relative;

  > :nth-child(1) {
    width: 100%;
    height: 100%;

    > p {
      padding-top: ${props => props.theme.layout.mobile ? 10 : 16}px;
      padding-left: ${props => props.theme.layout.mobile ? 47 : 75}px;
      text-align: left;
    }
  }
`;

const ReservationButton = styled.button`
  width: ${props => props.theme.layout.mobile ? 200 : 320}px;
  height: ${props => props.theme.layout.mobile ? 50 : 80}px;
  margin-bottom: ${props => props.theme.layout.mobile ? 6 : 51}px;
  background-color: #ffffff;
  display : ${Settings.reserve_now != null ? "" : "none"  };
`;

const ReservationParagraph = styled(Paragraph)`
  display : ${Settings.reserve_now != null ? "" : "none"  };
`;

const DivStamp = styled(BackgroundImage)`
  width: ${props => props.theme.layout.mobile ? 78 : 104}px;
  height: ${props => props.theme.layout.mobile ? 78 : 104}px;
  position: absolute;
  left: ${props => props.theme.layout.mobile ? 24 : 48}px;
  bottom: ${props => props.theme.layout.mobile ? -12 : -24}px;
  visibility: ${props => props.datavisible === true ? 'visible' : 'hidden'}
`;